import { useContext, useEffect, useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "design_system_radar/button";
import { Checkbox } from "design_system_radar/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design_system_radar/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design_system_radar/dialog";
import { EngineLiveIcon } from "design_system_radar/icons/EngineLiveIcon";
import { toast } from "design_system_radar/sonner";
import {
  CheckCircle2,
  Circle,
  CircleAlert,
  Clock3,
  LoaderCircle,
} from "lucide-react";

import { useReconciliationHistory } from "@/hooks/useReconciliationHistory";
import {
  type ReconciliationQueueItem,
  useReconciliationQueue,
} from "@/hooks/useReconciliationQueue";
import useAuth from "@/modules/auth/useAuth";
import { useReadOnlyUser } from "@/modules/auth/useReadOnlyUser";
import { ReconciliationServiceContext } from "@/provider/ReconciliationServiceContext";
import { fetchGroups } from "@/services/groups";
import { engineExecute } from "@/services/movements";

import { ReconciliationProgress } from "./admin/ReconciliationProgress";
import { LastReconciliation } from "./LastReconciliation";

interface ConciliarButtonProps {
  expanded?: boolean;
}

const QUEUE_STATUS_CONTENT = {
  pending: { label: "Pendiente", Icon: Circle },
  submitting: { label: "Iniciando", Icon: LoaderCircle },
  waiting: { label: "Esperando al motor", Icon: Clock3 },
  running: { label: "Conciliando", Icon: LoaderCircle },
  completed: { label: "Completado", Icon: CheckCircle2 },
  failed: { label: "Error", Icon: CircleAlert },
} as const;

function QueueStatus({ item }: { item: ReconciliationQueueItem }) {
  const { label, Icon } = QUEUE_STATUS_CONTENT[item.status];
  const isAnimated = item.status === "submitting" || item.status === "running";

  return (
    <div className="flex min-w-28 items-center justify-end gap-2 text-sm">
      <Icon
        aria-hidden="true"
        className={
          isAnimated
            ? "animate-spin text-radar-90 motion-reduce:animate-none"
            : item.status === "completed"
              ? "text-emerald-400"
              : item.status === "failed"
                ? "text-red-400"
                : "text-radar-white/65"
        }
        size={17}
      />
      <span>{label}</span>
    </div>
  );
}

export function ConciliarButton({ expanded = true }: ConciliarButtonProps) {
  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchGroups(),
  });
  const groups = groupsData?.data ?? [];
  const isReadOnlyUser = useReadOnlyUser();
  const { working, isConnected, setIsBatchRunning } = useContext(
    ReconciliationServiceContext,
  );
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const { lastCompletedAt, recordCompletion, now } = useReconciliationHistory(
    profile.id,
    open,
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const wasRunningRef = useRef(false);

  const { mutateAsync: executeGroup } = useMutation({
    mutationKey: ["reconciliation", "executeGroup"],
    mutationFn: (groupId: string) => engineExecute({ groups: [groupId] }),
    retry: false,
    // The queue renders per-group errors and a single summary for the batch.
    meta: { suppressErrorToast: true },
  });

  const queue = useReconciliationQueue({
    working,
    executeGroup,
    onCompleted: recordCompletion,
  });
  const selectedGroups = groups.filter((group) =>
    selectedGroupIds.has(group._id),
  );
  const hasQueueResults = queue.items.length > 0;

  useEffect(() => {
    setIsBatchRunning(queue.isRunning);
  }, [queue.isRunning, setIsBatchRunning]);

  useEffect(() => () => setIsBatchRunning(false), [setIsBatchRunning]);

  useEffect(() => {
    if (queue.isRunning) {
      wasRunningRef.current = true;
      return;
    }

    if (!wasRunningRef.current || queue.items.length === 0) return;

    wasRunningRef.current = false;
    if (queue.failedCount > 0) {
      toast.error(
        `${queue.failedCount} de ${queue.items.length} grupos no pudieron conciliarse`,
      );
      return;
    }

    toast.success(
      `${queue.items.length} ${queue.items.length === 1 ? "grupo conciliado" : "grupos conciliados"}`,
    );
  }, [queue.failedCount, queue.isRunning, queue.items.length]);

  const toggleGroup = (groupId: string) => {
    if (queue.isRunning) return;

    setSelectedGroupIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(groupId)) {
        nextIds.delete(groupId);
      } else {
        nextIds.add(groupId);
      }
      return nextIds;
    });
  };

  const resetSelection = () => {
    queue.reset();
    setSelectedGroupIds(new Set());
  };

  const startQueue = () => {
    if (
      selectedGroups.length === 0 ||
      queue.isRunning ||
      working ||
      !isConnected
    ) {
      return;
    }

    setIsBatchRunning(true);
    queue.start(
      selectedGroups.map((group) => ({ id: group._id, name: group.name })),
    );
  };

  const isDisabled = isReadOnlyUser || groups.length === 0;
  const triggerLabel = queue.isRunning
    ? `Conciliando ${queue.processedCount + 1}/${queue.items.length}`
    : "Conciliar";

  return (
    <Dialog
      open={open || queue.isRunning}
      onOpenChange={(nextOpen) => {
        if (!queue.isRunning) setOpen(nextOpen);
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="icon"
          className={expanded ? "h-9 w-34" : "h-9 w-12 rounded-xl p-0"}
          disabled={isDisabled}
          aria-label={expanded ? undefined : triggerLabel}
        >
          <div className="flex w-full items-center justify-center gap-2">
            {queue.isRunning ? (
              <LoaderCircle
                aria-hidden="true"
                className="animate-spin motion-reduce:animate-none"
                size={28}
              />
            ) : (
              <EngineLiveIcon size={28} />
            )}
            {expanded && triggerLabel}
          </div>
        </Button>
      </DialogTrigger>

      <DialogContent
        data-reconciliation-running={queue.isRunning}
        className={`scrollbar-blue max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] gap-0 overflow-y-auto p-0 ${hasQueueResults ? "sm:max-w-5xl md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]" : "sm:max-w-2xl"}`}
        showCloseButton={!queue.isRunning}
        onEscapeKeyDown={(event) => {
          if (queue.isRunning) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (queue.isRunning) event.preventDefault();
        }}
      >
        <div className="min-w-0">
          <DialogHeader className="px-6 pb-4 pt-6">
            <DialogTitle>Conciliar grupos</DialogTitle>
            <DialogDescription className="text-radar-white/70">
              {hasQueueResults
                ? "Los grupos se procesan uno por uno. Un error no detendrá los grupos restantes."
                : "Selecciona los grupos que quieres encolar. Se procesarán uno por uno en el orden de la lista."}
            </DialogDescription>
          </DialogHeader>

          {hasQueueResults ? (
            <div
              aria-live="polite"
              className="scrollbar-blue max-h-[55vh] overflow-y-auto px-6 py-4"
            >
              <div className="mb-3 flex items-center justify-between text-sm text-radar-white/70">
                <span>
                  {queue.processedCount} de {queue.items.length} procesados
                </span>
                {queue.failedCount > 0 && (
                  <span className="text-red-300">
                    {queue.failedCount} con error
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2" role="list">
                {queue.items.map((item) => (
                  <div
                    className="flex items-start justify-between gap-4 rounded-lg bg-radar-dark-blue/60 px-4 py-3"
                    key={item.id}
                    role="listitem"
                  >
                    <div className="min-w-0">
                      <p className="break-words font-medium text-radar-white">
                        {item.name}
                      </p>
                      {item.error && (
                        <p className="mt-1 text-sm text-red-300">
                          {item.error}
                        </p>
                      )}
                    </div>
                    <QueueStatus item={item} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="px-6 py-4">
              <Command className="h-auto overflow-hidden rounded-lg border border-radar-white/15 bg-radar-dark-blue/40">
                <CommandInput
                  aria-label="Buscar grupo"
                  placeholder="Buscar grupo..."
                />
                <CommandList className="scrollbar-blue max-h-[42vh]">
                  <CommandEmpty>No se encontraron grupos.</CommandEmpty>
                  <CommandGroup heading="Grupos disponibles">
                    {groups.map((group) => {
                      const isSelected = selectedGroupIds.has(group._id);

                      return (
                        <CommandItem
                          aria-selected={isSelected}
                          className="gap-3"
                          key={group._id}
                          onSelect={() => toggleGroup(group._id)}
                          value={`${group.name} ${group._id}`}
                        >
                          <Checkbox
                            aria-label={`Seleccionar ${group.name}`}
                            checked={isSelected}
                            className="pointer-events-none"
                            tabIndex={-1}
                          />
                          <span className="min-w-0 break-words">
                            <span>{group.name}</span>
                            <LastReconciliation
                              completedAt={lastCompletedAt(group._id)}
                              now={now}
                            />
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>

              <div className="mt-3 flex items-center justify-between gap-4 text-sm">
                <span className="text-radar-white/70">
                  {selectedGroups.length === 0
                    ? "Ningún grupo seleccionado"
                    : `${selectedGroups.length} ${selectedGroups.length === 1 ? "grupo seleccionado" : "grupos seleccionados"}`}
                </span>
                <div className="flex gap-3">
                  <button
                    className="text-radar-90 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedGroups.length === groups.length}
                    onClick={() =>
                      setSelectedGroupIds(
                        new Set(groups.map((group) => group._id)),
                      )
                    }
                    type="button"
                  >
                    Seleccionar todos
                  </button>
                  <button
                    className="text-radar-90 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={selectedGroups.length === 0}
                    onClick={() => setSelectedGroupIds(new Set())}
                    type="button"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              {!isConnected && (
                <p className="mt-4 rounded-lg bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                  La conexión en tiempo real no está disponible. Podrás iniciar
                  la cola cuando se restablezca.
                </p>
              )}
              {working && (
                <p className="mt-4 rounded-lg bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                  Ya hay una conciliación en curso. Espera a que termine para
                  iniciar este lote.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="px-6 py-4">
            {hasQueueResults && !queue.isRunning ? (
              <>
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Cerrar
                </Button>
                <Button onClick={resetSelection}>Conciliar otros grupos</Button>
              </>
            ) : hasQueueResults ? (
              <p className="text-sm text-radar-white/70">
                Espera a que termine el lote para continuar.
              </p>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  disabled={
                    selectedGroups.length === 0 || !isConnected || working
                  }
                  onClick={startQueue}
                >
                  {selectedGroups.length > 0
                    ? `Conciliar ${selectedGroups.length} ${selectedGroups.length === 1 ? "grupo" : "grupos"}`
                    : "Conciliar grupos"}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
        {hasQueueResults && (
          <aside className="flex min-w-0 items-center justify-center bg-[#171821] md:rounded-r-lg">
            <ReconciliationProgress
              isRunning={queue.isRunning}
              title={
                queue.isRunning ? "Conciliación en proceso" : "Lote finalizado"
              }
              description={
                queue.isRunning
                  ? `Procesando ${queue.items[queue.activeIndex ?? 0]?.name ?? "grupo"}. ${queue.processedCount} de ${queue.items.length} grupos procesados. La pantalla permanecerá bloqueada hasta finalizar el lote.`
                  : `${queue.completedCount} grupos completados${queue.failedCount ? ` y ${queue.failedCount} con error` : ""}. Ya puedes continuar.`
              }
            />
          </aside>
        )}
      </DialogContent>
    </Dialog>
  );
}
